import { COLORS } from '../../../IndividualPlan/tokens';
import type { PeDomainOverlayYear } from '../../types';
import { buildFlashlightTrajectory } from './flashlightTrajectoryData';

type FlashlightTrajectoryChartProps = {
  overlay: PeDomainOverlayYear;
  domainLabel: string;
  coverageThreshold?: number;
  fontSize?: number;
};

const WIDTH = 800;
const HEIGHT = 288;
const RUBRIC_MIN = 1;
const RUBRIC_MAX = 5;

export function FlashlightTrajectoryChart({
  overlay,
  domainLabel,
  coverageThreshold = 0.5,
  fontSize = 20,
}: FlashlightTrajectoryChartProps) {
  const { points, coverageText } = buildFlashlightTrajectory(
    overlay,
    domainLabel,
    coverageThreshold,
  );

  const left = 34;
  const right = 34;
  const top = 34;
  const bottom = 34;
  const innerW = WIDTH - left - right;
  const innerH = HEIGHT - top - bottom;

  const windowOrder = overlay.benchmark_windows.map((w) => w.window);
  const slotCount = windowOrder.length;

  const x = (window: string) => {
    if (slotCount <= 1) return left + innerW / 2;
    const ordinal = windowOrder.indexOf(window);
    return left + (ordinal / (slotCount - 1)) * innerW;
  };
  const y = (value: number) =>
    top + (1 - (value - RUBRIC_MIN) / (RUBRIC_MAX - RUBRIC_MIN)) * innerH;

  const polyline = points.map((p) => `${x(p.window)},${y(p.value)}`).join(' ');
  const dotRadius = 7;
  const valueGap = dotRadius + fontSize * 0.6;
  const lastWindow = points[points.length - 1]?.window;

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        style={{ height: 'auto', display: 'block' }}
        role="img"
        aria-label={`Flashlight ${domainLabel} benchmark trajectory`}
      >
        <line x1={0} y1={top + innerH} x2={WIDTH} y2={top + innerH} stroke={COLORS.hairline} />

        {points.length > 1 && (
          <polyline
            points={polyline}
            fill="none"
            stroke={COLORS.navy}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {points.map((p) => {
          const isLast = p.window === lastWindow;
          return (
            <circle
              key={`pt-${p.window}`}
              cx={x(p.window)}
              cy={y(p.value)}
              r={dotRadius}
              fill={isLast ? COLORS.navy : '#FFFFFF'}
              stroke={COLORS.navy}
              strokeWidth={2}
            />
          );
        })}

        {points.map((p) => (
          <text
            key={`value-${p.window}`}
            x={x(p.window)}
            y={y(p.value) - valueGap}
            textAnchor="middle"
            fill={COLORS.muted}
            fontSize={fontSize}
            fontWeight={500}
          >
            {p.value.toFixed(1)}
          </text>
        ))}

        {points.map((p) => (
          <text
            key={`xlabel-${p.window}`}
            x={x(p.window)}
            y={HEIGHT - 12}
            textAnchor="middle"
            fill={COLORS.muted}
            fontSize={fontSize}
            fontWeight={500}
          >
            {p.window}
          </text>
        ))}
      </svg>

      <p style={{ color: COLORS.muted, fontSize: 12, margin: '4px 0 0' }}>{coverageText}</p>
    </figure>
  );
}
